import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loadTestSession, saveAnswer as saveAnswerApi, finalizeTest } from '../logic/testLogic';
import { useAuth } from '../../shared/context/AuthContext';
import Timer from '../components/Timer';
import QuestionNav from '../components/QuestionNav';
import ViolationAlert from '../components/ViolationAlert';
import { useBrowserLock } from '../hooks/useBrowserLock';
import './TestPage.css';

const SAVE_DEBOUNCE_MS = 800;
const IMG_BASE = process.env.REACT_APP_IMG_BASE;

export default function TestPage({ onComplete }) {
  const { user, logout } = useAuth();

  // ── Session state ──────────────────────────────────────────
  const [phase, setPhase]           = useState('loading'); // loading | instructions | test | submitting | done
  const [session, setSession]       = useState(null);
  const [questions, setQuestions]   = useState([]);
  const [answers, setAnswers]       = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');
  const [isTerminated, setIsTerminated] = useState(false);

  // ── Violations ────────────────────────────────────────────
  const [violations, setViolations]         = useState(0);
  const [maxViolations, setMaxViolations]   = useState(3);
  const [lastViolation, setLastViolation]   = useState(null);
  const [showAlert, setShowAlert]           = useState(false);
  const [alertKey, setAlertKey]             = useState(0);

  // ── Fullscreen prompt ──────────────────────────────────────
  const [fsPrompt, setFsPrompt] = useState(false);

  // ── Confirm submit modal ───────────────────────────────────
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const saveTimerRef = useRef(null);
  const exitFsRef = useRef(() => {});

  // ── Load or start session ─────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { payload, restoredAnswers } = await loadTestSession();
        setSession(payload);
        setQuestions(payload.questions);
        setAnswers(restoredAnswers);
        setMaxViolations(payload.config?.max_violations || 3);
        setPhase('instructions');
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to load test';
        setError(msg);
        setPhase('error');
      }
    })();
  }, []);

  // ── Violation handler ─────────────────────────────────────
  const handleViolation = useCallback((count, max, type) => {
    setViolations(count);
    setMaxViolations(max);
    setLastViolation(type);
    setAlertKey(k => k + 1);
    setShowAlert(true);
  }, []);

  // ── Submit ────────────────────────────────────────────────
  const submitTest = useCallback(async (forced = false) => {
    if (!session) return;
    if (forced) setIsTerminated(true);
    exitFsRef.current();
    setPhase('submitting');
    try {
      const result = await finalizeTest(session.sessionId);
      setResult(result);
      setPhase('done');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Submission failed');
      setPhase('error');
    }
  }, [session]);

  const handleForceSubmit = useCallback(() => {
    submitTest(true);
  }, [submitTest]);

  const handleFullscreenExit = useCallback(() => {
    setFsPrompt(true);
  }, []);

  // ── Browser lock (active only during test phase) ──────────
  const { requestFullscreen, exitFullscreen, isFullscreen, isBlocked, unblock } = useBrowserLock({
    sessionId:        session?.sessionId,
    onViolation:      handleViolation,
    onForceSubmit:    handleForceSubmit,
    onFullscreenExit: handleFullscreenExit,
    enabled:          phase === 'test',
  });

  exitFsRef.current = exitFullscreen;

  // ── Enter fullscreen when test starts ─────────────────────
  const startTest = () => {
    requestFullscreen();
    setPhase('test');
  };

  const returnToFullscreen = () => {
    requestFullscreen();
    setFsPrompt(false);
  };

  // ── Save answer (debounced) ───────────────────────────────
  const saveAnswer = useCallback((questionId, option) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveAnswerApi(session.sessionId, questionId, option);
      } catch { /* silent */ }
    }, SAVE_DEBOUNCE_MS);
  }, [session]);

  const selectAnswer = (option) => {
    const qId = questions[currentIdx]?.id;
    if (!qId) return;
    const newAnswers = { ...answers, [qId]: option };
    setAnswers(newAnswers);
    saveAnswer(qId, option);
  };

  // ── Navigation ────────────────────────────────────────────
  const goTo = (idx) => {
    if (idx >= 0 && idx < questions.length) setCurrentIdx(idx);
  };

  // ── Timer expire ──────────────────────────────────────────
  const handleTimerExpire = useCallback(() => {
    submitTest(false);
  }, [submitTest]);

  // ══════════════════════════════════════════════════════════
  // RENDER PHASES
  // ══════════════════════════════════════════════════════════

  if (phase === 'loading') {
    return (
      <div className="test-center">
        <div className="spinner" />
        <p style={{ color: 'var(--muted)', marginTop: '1rem' }}>Loading your test…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="test-center">
        <div className="error-box fade-up">
          <div className="error-icon">⚠</div>
          <h2>Unable to Load Test</h2>
          <p>{error}</p>
          <button className="btn btn-outline" onClick={logout}>Back to Login</button>
        </div>
      </div>
    );
  }

  if (phase === 'instructions') {
    return (
      <div className="test-center">
        <div className="instructions-card fade-up">
          <div className="inst-header">
            <div className="inst-logo">📋</div>
            <h1>Test Instructions</h1>
            <p>Please read carefully before starting</p>
          </div>

          <div className="inst-meta">
            <div className="inst-meta-item">
              <span className="meta-icon">⏱</span>
              <div>
                <strong>{session?.config?.duration_minutes} minutes</strong>
                <span>Duration</span>
              </div>
            </div>
            <div className="inst-meta-item">
              <span className="meta-icon">❓</span>
              <div>
                <strong>{questions.length} Questions</strong>
                <span>Total</span>
              </div>
            </div>
            <div className="inst-meta-item">
              <span className="meta-icon">🎯</span>
              <div>
                <strong>Single Attempt</strong>
                <span>No retry</span>
              </div>
            </div>
          </div>

          <ul className="inst-rules">
            {[
              'The test must be taken in fullscreen mode. Exiting fullscreen is a violation.',
              'Do not switch tabs, windows, minimize, or open other applications during the test.',
              'Copy, paste, cut, and right-click are disabled throughout the test.',
              'Browser navigation (back/forward/refresh) is blocked.',
              'Opening the test in multiple tabs or devices is prohibited.',
              'Developer tools are detected and prohibited.',
              `Exceeding ${maxViolations} violations will automatically terminate and submit your test.`,
              'The test auto-submits when the timer reaches zero.',
              'Your answers are auto-saved as you select them.',
              'Once submitted, you cannot retake or resume the test.',
            ].map((rule, i) => (
              <li key={i}><span className="rule-num">{i + 1}</span>{rule}</li>
            ))}
          </ul>

          <div className="inst-acknowledge">
            <div className="inst-warning">
              <strong>⚠ Important:</strong> By starting, you agree to attempt this test honestly. All suspicious actions are logged in real time and associated with your session.
            </div>
            <button className="btn btn-primary" onClick={startTest} style={{ width: '100%', padding: '1rem' }}>
              🚀 Start Test Now (Enters Fullscreen)
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'submitting') {
    return (
      <div className="test-center">
        <div className="spinner" />
        <p style={{ color: 'var(--muted)', marginTop: '1rem' }}>Submitting your test…</p>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="test-center">
        <div className="result-card fade-up">
          <div className="completion-icon">
            {isTerminated ? '🚫' : '✅'}
          </div>

          <h1 className="result-title">
            {isTerminated ? 'Test Terminated' : 'Test Submitted Successfully'}
          </h1>

          {isTerminated ? (
            <>
              <p className="result-subtitle">
                Your test session was automatically terminated due to exceeding the allowed number of violations.
              </p>
              <div className="terminated-banner">
                🚫 TERMINATED DUE TO VIOLATIONS
              </div>
            </>
          ) : (
            <p className="result-subtitle">
              Thank you for completing the assessment. Your responses have been securely recorded.
            </p>
          )}

          <div className="completion-steps">
            <div className="completion-step">
              <span className="cs-icon">📋</span>
              <div>
                <strong>Responses Recorded</strong>
                <span>All your answers have been saved and submitted successfully.</span>
              </div>
            </div>
            <div className="completion-step">
              <span className="cs-icon">🔍</span>
              <div>
                <strong>Under Review</strong>
                <span>Your assessment is being reviewed by our evaluation team.</span>
              </div>
            </div>
            <div className="completion-step">
              <span className="cs-icon">📬</span>
              <div>
                <strong>Next Steps</strong>
                <span>The administrator will get in touch with you regarding the outcome.</span>
              </div>
            </div>
          </div>

          <p className="result-note">
            Please do not attempt to retake the test. Only one attempt is permitted per candidate.
          </p>

          <button className="btn btn-outline" onClick={logout}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN TEST PHASE ────────────────────────────────────────
  const currentQ      = questions[currentIdx];
  const OPTIONS       = ['A', 'B', 'C', 'D'];
  const optionTexts   = [currentQ?.option_a, currentQ?.option_b, currentQ?.option_c, currentQ?.option_d];
  const optionImages  = [currentQ?.option_a_image, currentQ?.option_b_image, currentQ?.option_c_image, currentQ?.option_d_image];
  const selected      = currentQ ? answers[currentQ.id] : null;
  const answeredCount = Object.keys(answers).length;

  const violationRatio    = violations / maxViolations;
  const violationSeverity = violationRatio >= 1 ? 'danger' : violationRatio >= 0.5 ? 'warn' : '';

  return (
    <div className="test-layout" onContextMenu={e => e.preventDefault()}>

      {/* ── Violation alert toast ──────────────────────────── */}
      <ViolationAlert
        key={alertKey}
        count={violations}
        max={maxViolations}
        lastType={lastViolation}
        onDismiss={() => setShowAlert(false)}
      />

      {/* ── TAB SWITCH / FULLSCREEN EXIT LOCKOUT ───────────── */}
      {(isBlocked || fsPrompt) && (
        <div className="modal-overlay fs-prompt-overlay" style={{ zIndex: 99999, backdropFilter: 'blur(12px)', background: 'rgba(0,0,0,0.92)' }}>
          <div className="modal-box fs-prompt-box fade-up" style={{ maxWidth: 420, textAlign: 'center' }}>
            <div className="fs-prompt-icon" style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🔒</div>
            <h3 style={{ margin: '0 0 0.5rem', color: '#f87171', fontSize: '1.3rem' }}>Test Locked</h3>
            <p style={{ color: '#94a3b8', marginBottom: '1.25rem', lineHeight: 1.6 }}>
              You <strong style={{ color: '#fbbf24' }}>left the exam window</strong> or exited fullscreen.
              This has been recorded as a violation. Return to fullscreen to continue.
            </p>
            <div className="fs-violation-count" style={{ marginBottom: '1.5rem' }}>
              <span className={violationSeverity} style={{ fontSize: '0.95rem' }}>
                ⚠ {violations} / {maxViolations} violations used
              </span>
              {violations >= maxViolations - 1 && violations < maxViolations && (
                <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0.5rem 0 0', fontWeight: 600 }}>
                  ⚡ One more violation will auto-submit your test!
                </p>
              )}
            </div>
            {violations < maxViolations ? (
              <button
                className="btn btn-primary"
                style={{ width: '100%', fontSize: '1rem', padding: '0.85rem', background: '#2563eb' }}
                onClick={() => { unblock(); setFsPrompt(false); }}
              >
                ⛶ Return to Fullscreen & Continue
              </button>
            ) : (
              <p style={{ color: '#ef4444', fontWeight: 700, textAlign: 'center' }}>
                Violation limit reached — test is being submitted…
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm submit modal ───────────────────────────── */}
      {confirmSubmit && (
        <div className="modal-overlay" onClick={() => setConfirmSubmit(false)}>
          <div className="modal-box fade-up" onClick={e => e.stopPropagation()}>
            <h3>Submit Test?</h3>
            <p>You have answered <strong>{answeredCount}</strong> of <strong>{questions.length}</strong> questions.</p>
            {answeredCount < questions.length && (
              <p className="modal-warn">⚠ {questions.length - answeredCount} question(s) unanswered.</p>
            )}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setConfirmSubmit(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { setConfirmSubmit(false); submitTest(false); }}>
                Submit Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="test-header">
        <div className="th-left">
          <div className="th-logo">ATP</div>
          <div className="th-info">
            <span className="th-name">{user?.profile?.full_name || user?.username}</span>
            <span className="th-qcount">Q {currentIdx + 1} / {questions.length}</span>
          </div>
        </div>

        <div className="th-center">
          {/* Fullscreen status indicator */}
          <div
            className={`fs-status-pill ${isFullscreen ? 'fs-active' : 'fs-inactive'}`}
            title={isFullscreen ? 'Fullscreen active' : 'Not in fullscreen — click to restore'}
            onClick={!isFullscreen ? returnToFullscreen : undefined}
            style={{ cursor: isFullscreen ? 'default' : 'pointer' }}
          >
            <span className="fs-status-dot" />
            {isFullscreen ? 'Fullscreen' : 'Not Fullscreen — Click to Restore'}
          </div>

          {/* Violation badge */}
          <div className="violation-badge" title="Violations used">
            <span className={violationSeverity}>
              ⚠ {violations}/{maxViolations} violations
            </span>
          </div>
        </div>

        <div className="th-right">
          {session?.expiresAt && (
            <Timer expiresAt={session.expiresAt} onExpire={handleTimerExpire} />
          )}
          <button className="btn btn-danger btn-sm" onClick={() => setConfirmSubmit(true)}>
            Submit
          </button>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────── */}
      <div className="test-body">
        {/* Question area */}
        <main className="test-main">
          <div className="question-card fade-up" key={currentIdx}>
            <div className="qcard-header">
              <div className="qcard-num">Question {currentIdx + 1}</div>
              {currentQ?.category && (
                <span className="badge badge-purple">{currentQ.category}</span>
              )}
              <span className="badge badge-gold">{currentQ?.marks || 1} mark{(currentQ?.marks || 1) !== 1 ? 's' : ''}</span>
              {selected && <span className="badge badge-green">Answered</span>}
            </div>

            <div className="qcard-text">
              <p>{currentQ?.question_text}</p>
              {currentQ?.question_image && (
                <div className="qcard-img-wrap">
                  <img src={`${IMG_BASE}${currentQ.question_image}`} alt="Question" className="qcard-img"/>
                </div>
              )}
            </div>

            <div className="qcard-options">
              {OPTIONS.map((opt, i) => (
                <button
                  key={opt}
                  className={`option-btn ${selected === opt ? 'selected' : ''}`}
                  onClick={() => selectAnswer(opt)}
                >
                  <span className="opt-label">{opt}</span>
                  <div className="opt-body">
                    <span className="opt-text">{optionTexts[i]}</span>
                    {optionImages[i] && <img src={`${IMG_BASE}${optionImages[i]}`} alt={`Option ${opt}`} className="opt-img"/>}
                  </div>
                  {selected === opt && <span className="opt-check">✓</span>}
                </button>
              ))}
            </div>

            {selected && (
              <button
                className="btn btn-outline btn-sm clear-btn"
                onClick={() => {
                  const qId = currentQ.id;
                  const updated = { ...answers };
                  delete updated[qId];
                  setAnswers(updated);
                  saveAnswer(qId, null);
                }}
              >
                Clear Answer
              </button>
            )}
          </div>

          <div className="qnav-buttons">
            <button
              className="btn btn-outline"
              onClick={() => goTo(currentIdx - 1)}
              disabled={currentIdx === 0}
            >
              ← Previous
            </button>

            <span className="qnav-hint">
              {currentIdx + 1} of {questions.length}
            </span>

            {currentIdx < questions.length - 1 ? (
              <button className="btn btn-primary" onClick={() => goTo(currentIdx + 1)}>
                Next →
              </button>
            ) : (
              <button className="btn btn-danger" onClick={() => setConfirmSubmit(true)}>
                Submit Test
              </button>
            )}
          </div>
        </main>

        {/* Sidebar */}
        <aside className="test-sidebar">
          <QuestionNav
            questions={questions}
            currentIndex={currentIdx}
            answers={answers}
            onJump={goTo}
          />

          <div className="sidebar-progress">
            <div className="sp-label">
              <span>Progress</span>
              <span>{Math.round((answeredCount / questions.length) * 100)}%</span>
            </div>
            <div className="sp-bar">
              <div className="sp-fill" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
            </div>
          </div>

          {/* Violation sidebar summary */}
          <div className={`sidebar-violations ${violationSeverity}`}>
            <div className="sv-header">
              <span>Violations</span>
              <span className="sv-count">{violations} / {maxViolations}</span>
            </div>
            <div className="sv-bar">
              <div
                className="sv-fill"
                style={{ width: `${Math.min((violations / maxViolations) * 100, 100)}%` }}
              />
            </div>
            <p className="sv-note">
              {maxViolations - violations > 0
                ? `${maxViolations - violations} warning${maxViolations - violations !== 1 ? 's' : ''} remaining before auto-submit`
                : 'Violation limit reached!'}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
